import React, { useEffect, useRef, useContext } from "react";
import {
  NatureOutlined, ChildCareOutlined, WifiOutlined,
  DeckOutlined, DirectionsRunOutlined,
} from "@mui/icons-material";
import gsap from "gsap";
import greenspaces    from "../Images/green spaces.jpg";
import kidsarea       from "../Images/kidsarea.jpg";
import smartsystem    from "../Images/smart systems.jpg";
import outdoorseeting from "../Images/outdoor seeting.jpg";
import walking        from "../Images/walking.jpg";
import { LanguageContext } from "./LanguageContext";

const GAP   = 20;
const SPEED = 0.5;

// ========== Falling Leaves Canvas ==========
const FallingLeaves = ({ containerRef }) => {
  const canvasRef    = useRef(null);
  const animFrameRef = useRef(null);
  const leavesRef    = useRef([]);

  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef?.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const resize = () => {
      // ✅ عرض وارتفاع الـ section مش الـ window
      canvas.width  = container ? container.offsetWidth  : window.innerWidth;
      canvas.height = container ? container.offsetHeight : window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = [
      "#4a7c3f", "#5a9e4a", "#3d6b35", "#6aad55",
      "#8B6914", "#a07830", "#c49a3c", "#7a5c1e",
      "#6b8e3f", "#9db85a",
    ];

    function shadeColor(hex, amount) {
      const num = parseInt(hex.replace("#", ""), 16);
      const r   = Math.max(0, Math.min(255, (num >> 16)         + amount));
      const g   = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
      const b   = Math.max(0, Math.min(255, (num & 0xff)        + amount));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
    }

    const drawLeaf = (ctx, leaf) => {
      ctx.save();
      ctx.translate(leaf.x, leaf.y);
      ctx.rotate(leaf.rotation);
      ctx.scale(leaf.scaleX, leaf.scaleY);

      const s = leaf.size;
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.bezierCurveTo( s * 0.6, -s * 0.5,  s * 0.8,  s * 0.3, 0,  s);
      ctx.bezierCurveTo(-s * 0.8,  s * 0.3, -s * 0.6, -s * 0.5, 0, -s);
      ctx.closePath();

      const grad = ctx.createLinearGradient(-s * 0.5, -s, s * 0.5, s);
      grad.addColorStop(0,   leaf.color     + "ee");
      grad.addColorStop(0.5, leaf.color     + "cc");
      grad.addColorStop(1,   leaf.colorDark + "99");
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.quadraticCurveTo(s * 0.1, 0, 0, s);
      ctx.strokeStyle = leaf.colorDark + "88";
      ctx.lineWidth   = s * 0.06;
      ctx.stroke();

      for (let i = -1; i <= 1; i += 0.5) {
        const py = i * s * 0.4;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.quadraticCurveTo( s * 0.4, py - s * 0.1,  s * 0.55, py);
        ctx.strokeStyle = leaf.colorDark + "44";
        ctx.lineWidth   = s * 0.03;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.quadraticCurveTo(-s * 0.4, py - s * 0.1, -s * 0.55, py);
        ctx.stroke();
      }

      ctx.restore();
    };

    const GROUND_Y = () => canvas.height - 18;

    const createLeaf = (startFromTop = true) => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      return {
        x:            Math.random() * canvas.width,
        y:            startFromTop
                        ? -30 - Math.random() * 120
                        : Math.random() * canvas.height,
        size:         8 + Math.random() * 14, // ✅ أصغر شوية من الهيرو
        speedY:       0.5 + Math.random() * 1,
        speedX:       (Math.random() - 0.5) * 0.7,
        rotation:     Math.random() * Math.PI * 2,
        rotSpeed:     (Math.random() - 0.5) * 0.04,
        scaleX:       1,
        scaleY:       1,
        scaleDir:     Math.random() > 0.5 ? 1 : -1,
        scaleSpeed:   0.01 + Math.random() * 0.02,
        swingAmp:     0.3  + Math.random() * 0.5,
        swingSpeed:   0.01 + Math.random() * 0.02,
        swingOffset:  Math.random() * Math.PI * 2,
        color,
        colorDark:    shadeColor(color, -40),
        opacity:      0.6 + Math.random() * 0.3, // ✅ أخف شوية
        time:         0,
        state:        "falling",
        restTimer:    0,
        restDuration: 120 + Math.floor(Math.random() * 200),
      };
    };

    // ابدأ بـ 12 ورقة موزعة
    leavesRef.current = Array.from({ length: 12 }, () => createLeaf(false));

    let frameCount = 0;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;

      if (frameCount % 100 === 0) {
        leavesRef.current.push(createLeaf(true));
      }

      leavesRef.current = leavesRef.current.filter((leaf) => {
        leaf.time++;
        const ground = GROUND_Y();

        if (leaf.state === "falling") {
          leaf.y        += leaf.speedY;
          leaf.x        += leaf.speedX + Math.sin(leaf.time * leaf.swingSpeed + leaf.swingOffset) * leaf.swingAmp;
          leaf.rotation += leaf.rotSpeed;

          leaf.scaleX += leaf.scaleDir * leaf.scaleSpeed;
          if (Math.abs(leaf.scaleX) >= 1) leaf.scaleDir *= -1;

          if (leaf.y > ground - 80) {
            const t       = Math.max(0, (leaf.y - (ground - 80)) / 80);
            leaf.speedY   = Math.max(0.05, leaf.speedY  * (1 - t * 0.05));
            leaf.speedX  *= 0.98;
            leaf.rotSpeed *= 0.97;
          }

          if (leaf.y >= ground) {
            leaf.y        = ground;
            leaf.speedY   = 0;
            leaf.speedX   = 0;
            leaf.rotSpeed = 0;
            leaf.state    = "resting";
          }

        } else if (leaf.state === "resting") {
          leaf.restTimer++;
          if (leaf.restTimer >= leaf.restDuration) leaf.state = "fading";

        } else if (leaf.state === "fading") {
          leaf.opacity -= 0.01;
          if (leaf.opacity <= 0) return false;
        }

        ctx.globalAlpha = Math.max(0, leaf.opacity);
        drawLeaf(ctx, leaf);
        ctx.globalAlpha = 1;
        return true;
      });
    };

    animate();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      "absolute",
        inset:         0,
        zIndex:        5,          // ✅ فوق الكروت
        pointerEvents: "none",     // ✅ مش بتاخد clicks
        borderRadius:  "inherit",
      }}
    />
  );
};

// ========== AutoScrollAmenities ==========
const AutoScrollAmenities = () => {
  const { language } = useContext(LanguageContext);

  const sectionRef   = useRef(null);
  const headerRef    = useRef(null);
  const containerRef = useRef(null);
  const cardRefsA    = useRef([]);
  const cardRefsB    = useRef([]);

  const rafRef    = useRef(null);
  const pausedRef = useRef(false);
  const posARef   = useRef(null);
  const posBRef   = useRef(null);

  const isRTL = language === "ar";

  const translations = {
    en: {
      eyebrow: "Life at Masar",
      heading: "World-Class Amenities",
      sub:     "Every detail designed for an extraordinary lifestyle",
      items: [
        { title: "Green Spaces",            img: greenspaces,     icon: <NatureOutlined        sx={{ fontSize: "1.8rem" }} /> },
        { title: "Kids' Play Area",         img: kidsarea,        icon: <ChildCareOutlined     sx={{ fontSize: "1.8rem" }} /> },
        { title: "Smart Systems",           img: smartsystem,     icon: <WifiOutlined          sx={{ fontSize: "1.8rem" }} /> },
        { title: "Outdoor Seating",         img: outdoorseeting,  icon: <DeckOutlined          sx={{ fontSize: "1.8rem" }} /> },
        { title: "Jogging & Walking Track", img: walking,         icon: <DirectionsRunOutlined sx={{ fontSize: "1.8rem" }} /> },
      ],
    },
    ar: {
      eyebrow: "الحياة في مسار",
      heading: "مرافق عالمية المستوى",
      sub:     "كل تفصيلة صُمِّمت لأسلوب حياة استثنائي",
      items: [
        { title: "مساحات خضراء",          img: greenspaces,     icon: <NatureOutlined        sx={{ fontSize: "1.8rem" }} /> },
        { title: "منطقة ألعاب للأطفال",  img: kidsarea,        icon: <ChildCareOutlined     sx={{ fontSize: "1.8rem" }} /> },
        { title: "أنظمة ذكية",            img: smartsystem,     icon: <WifiOutlined          sx={{ fontSize: "1.8rem" }} /> },
        { title: "منطقة جلوس خارجية",    img: outdoorseeting,  icon: <DeckOutlined          sx={{ fontSize: "1.8rem" }} /> },
        { title: "مسار للجري والمشي",     img: walking,         icon: <DirectionsRunOutlined sx={{ fontSize: "1.8rem" }} /> },
      ],
    },
  };

  const { eyebrow, heading, sub, items } = translations[language];

  // ─── Header animation ─────────────────────────────────────────
  useEffect(() => {
    const section = sectionRef.current;
    const header  = headerRef.current;
    if (!section || !header) return;
    gsap.set(header, { opacity: 0, y: 35 });
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) gsap.to(header, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" });
        else gsap.set(header, { opacity: 0, y: 35 });
      });
    }, { threshold: 0.1 });
    obs.observe(section);
    return () => obs.disconnect();
  }, [language]);

  // ─── Seamless loop ─────────────────────────────────────────────
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    pausedRef.current = false;

    const refsA = cardRefsA.current.filter(Boolean);
    const refsB = cardRefsB.current.filter(Boolean);
    if (!refsA.length || !refsB.length) return;

    const imgs = Array.from(containerRef.current?.querySelectorAll("img") || []);
    const loaded = imgs.map(
      (img) => new Promise((res) => {
        if (img.complete) res();
        else { img.onload = res; img.onerror = res; }
      })
    );

    Promise.all(loaded).then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const CARD_W = refsA[0]?.offsetWidth || 200;
        const STEP   = CARD_W + GAP;
        const TOTAL  = STEP * items.length;

        posARef.current = refsA.map((_, i) => i * STEP);
        posBRef.current = refsB.map((_, i) => TOTAL + i * STEP);

        refsA.forEach((el, i) => {
          el.style.position = "absolute";
          el.style.left     = `${posARef.current[i]}px`;
          el.style.top      = "8px";
        });
        refsB.forEach((el, i) => {
          el.style.position = "absolute";
          el.style.left     = `${posBRef.current[i]}px`;
          el.style.top      = "8px";
        });

        const cardH = refsA[0]?.offsetHeight || 280;
        if (containerRef.current) {
          containerRef.current.style.height = `${cardH + 28}px`;
        }

        const tick = () => {
          if (!pausedRef.current) {
            posARef.current = posARef.current.map((x, i) => {
              let nx = x - SPEED;
              if (nx + CARD_W < 0) nx += TOTAL * 2;
              refsA[i].style.left = `${nx}px`;
              return nx;
            });
            posBRef.current = posBRef.current.map((x, i) => {
              let nx = x - SPEED;
              if (nx + CARD_W < 0) nx += TOTAL * 2;
              refsB[i].style.left = `${nx}px`;
              return nx;
            });
          }
          rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
      }));
    });

    return () => cancelAnimationFrame(rafRef.current);
  }, [language, items.length]);

  const handlePause    = () => { pausedRef.current = true; };
  const handleResume   = () => { pausedRef.current = false; };
  const handleTouchEnd = () => setTimeout(handleResume, 1500);

  const renderCard = (item, refArr, idx, keyPrefix) => (
    <div
      key={`${keyPrefix}-${idx}`}
      ref={(el) => (refArr.current[idx] = el)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-6px) scale(1.02)";
        e.currentTarget.style.boxShadow = "0 16px 40px rgba(74,124,63,0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.07)";
      }}
      style={{
        width:        "200px",
        borderRadius: "18px",
        overflow:     "hidden",
        background:   "rgba(255,255,255,0.93)",
        border:       "1px solid rgba(74,124,63,0.13)",
        boxShadow:    "0 4px 18px rgba(0,0,0,0.07)",
        transition:   "transform 0.3s ease, box-shadow 0.3s ease",
        willChange:   "left",
      }}
    >
      <div style={{ position: "relative", overflow: "hidden", height: "148px" }}>
        <img src={item.img} alt={item.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(15,31,15,0.38) 0%, transparent 60%)",
        }} />
      </div>
      <div style={{ padding: "14px 16px 16px", textAlign: "center" }}>
        <div style={{
          width: "44px", height: "44px", borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(74,124,63,0.12), rgba(106,173,85,0.2))",
          border: "1px solid rgba(74,124,63,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 8px", color: "#4a7c3f",
        }}>
          {item.icon}
        </div>
        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f1f0f" }}>
          {item.title}
        </div>
      </div>
    </div>
  );

  return (
    <section
      id="amenities"
      ref={sectionRef}
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        position:   "relative",
        padding:    "80px 0 70px",
        background: "linear-gradient(160deg, #f0f7f0 0%, #ffffff 60%, #eaf3e0 100%)",
        fontFamily: "'Cairo', sans-serif",
        overflow:   "hidden",
      }}
    >
      {/* ✅ Falling Leaves على مستوى الـ section كله */}
      <FallingLeaves containerRef={sectionRef} />

      {/* Wavy top */}
      <div style={{
        position: "absolute", top: "-58px", left: 0, right: 0,
        height: "80px", overflow: "hidden", zIndex: 1, pointerEvents: "none",
      }}>
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}>
          <path d="M0,0 L1440,0 L1440,25 Q1260,80 1080,42 Q900,8 720,50 Q540,88 360,42 Q180,0 0,50 Z"
            fill="#f4f8f4" />
        </svg>
      </div>

      {/* Header */}
      <div ref={headerRef} style={{
        textAlign: "center", marginBottom: "48px",
        padding: "0 20px", position: "relative", zIndex: 2, opacity: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center",
          justifyContent: "center", gap: "10px", marginBottom: "12px" }}>
          <span style={{ display: "inline-block", width: "36px", height: "2px",
            background: "linear-gradient(to right, transparent, #4a7c3f)" }} />
          <span style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.18em",
            textTransform: "uppercase", color: "#4a7c3f" }}>
            🌿 {eyebrow}
          </span>
          <span style={{ display: "inline-block", width: "36px", height: "2px",
            background: "linear-gradient(to left, transparent, #4a7c3f)", opacity: 0.6 }} />
        </div>
        <h2 style={{ fontWeight: 900, fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
          color: "#0f1f0f", lineHeight: 1.2, marginBottom: "10px" }}>
          {heading}
        </h2>
        <p style={{ fontSize: "clamp(0.9rem, 2vw, 1rem)", color: "#4a5a4a", margin: 0 }}>
          {sub}
        </p>
      </div>

      {/* Track wrapper */}
      <div
        style={{ position: "relative", zIndex: 2, overflow: "hidden" }}
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
        onTouchStart={handlePause}
        onTouchEnd={handleTouchEnd}
      >
        {/* Fade edges */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 4, pointerEvents: "none",
          background: "linear-gradient(to right, #f0f7f0 0%, transparent 8%, transparent 92%, #eaf3e0 100%)",
        }} />

        <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
          {items.map((item, i) => renderCard(item, cardRefsA, i, "a"))}
          {items.map((item, i) => renderCard(item, cardRefsB, i, "b"))}
        </div>
      </div>
    </section>
  );
};

export default AutoScrollAmenities;